import apiClient from './client';
import type {
  CrossAnalysisResponse,
  CrossAnalysisDimension,
  CrossAnalysisMetric,
  InflowAnalysisResponse,
  FunnelDefinition,
  FunnelDefinitionCreate,
  FunnelAnalysisResponse,
  URLClickAnalysisResponse,
  ScoringRule,
  ScoringRuleCreate,
  ScoringResultResponse,
  ScoreRankingResponse,
  ConversionGoal,
  ConversionGoalCreate,
  ConversionTrendResponse,
  ConversionSummaryResponse,
} from '@/types/data';

export interface CrossAnalysisParams {
  company_id: number;
  dimension1: CrossAnalysisDimension;
  dimension2: CrossAnalysisDimension;
  metric?: CrossAnalysisMetric;
  start_date: string;
  end_date: string;
  account_ids?: number[];
  categories?: string[];
}

export interface InflowAnalysisParams {
  company_id: number;
  start_date: string;
  end_date: string;
  account_ids?: number[];
}

export interface FunnelAnalysisParams {
  funnel_id: number;
  start_date: string;
  end_date: string;
  account_ids?: number[];
}

export interface URLClickAnalysisParams {
  company_id: number;
  start_date: string;
  end_date: string;
  channel?: string;
  account_ids?: number[];
}

export const analysisApi = {
  // クロス分析
  getCrossAnalysis: async (params: CrossAnalysisParams): Promise<CrossAnalysisResponse> => {
    const response = await apiClient.get<CrossAnalysisResponse>('/api/analysis/cross-analysis', { params });
    return response.data;
  },

  // 流入経路分析
  getInflowAnalysis: async (params: InflowAnalysisParams): Promise<InflowAnalysisResponse> => {
    const response = await apiClient.get<InflowAnalysisResponse>('/api/analysis/inflow-analysis', { params });
    return response.data;
  },

  // URLクリック分析
  getURLClickAnalysis: async (params: URLClickAnalysisParams): Promise<URLClickAnalysisResponse> => {
    const response = await apiClient.get<URLClickAnalysisResponse>('/api/analysis/url-click-analysis', { params });
    return response.data;
  },
};

export const funnelApi = {
  // ファネル定義一覧取得
  getDefinitions: async (companyId: number, isActive?: boolean): Promise<FunnelDefinition[]> => {
    const params: Record<string, unknown> = { company_id: companyId };
    if (isActive !== undefined) {
      params.is_active = isActive;
    }
    const response = await apiClient.get<FunnelDefinition[]>('/api/funnel/definitions', { params });
    return response.data;
  },

  // ファネル定義作成
  createDefinition: async (data: FunnelDefinitionCreate): Promise<FunnelDefinition> => {
    const response = await apiClient.post<FunnelDefinition>('/api/funnel/definitions', data);
    return response.data;
  },

  // ファネル定義詳細取得
  getDefinition: async (funnelId: number): Promise<FunnelDefinition> => {
    const response = await apiClient.get<FunnelDefinition>(`/api/funnel/definitions/${funnelId}`);
    return response.data;
  },

  // ファネル定義更新
  updateDefinition: async (funnelId: number, data: FunnelDefinitionCreate): Promise<FunnelDefinition> => {
    const response = await apiClient.put<FunnelDefinition>(`/api/funnel/definitions/${funnelId}`, data);
    return response.data;
  },

  // ファネル定義削除
  deleteDefinition: async (funnelId: number): Promise<void> => {
    await apiClient.delete(`/api/funnel/definitions/${funnelId}`);
  },

  // ファネル分析実行
  analyze: async (params: FunnelAnalysisParams): Promise<FunnelAnalysisResponse> => {
    const { funnel_id, ...queryParams } = params;
    const response = await apiClient.get<FunnelAnalysisResponse>(`/api/funnel/${funnel_id}/analysis`, { params: queryParams });
    return response.data;
  },
};

// スコアリングAPIパラメータ
export interface ScoringCalculateParams {
  rule_id: number;
  start_date: string;
  end_date: string;
  account_ids?: number[];
}

export interface ScoringRankingParams {
  rule_id: number;
  start_date: string;
  end_date: string;
  limit?: number;
  account_ids?: number[];
}

export const scoringApi = {
  // スコアルール一覧取得
  getRules: async (companyId: number, isActive?: boolean): Promise<ScoringRule[]> => {
    const params: Record<string, unknown> = { company_id: companyId };
    if (isActive !== undefined) {
      params.is_active = isActive;
    }
    const response = await apiClient.get<ScoringRule[]>('/api/scoring/rules', { params });
    return response.data;
  },

  // スコアルール作成
  createRule: async (data: ScoringRuleCreate): Promise<ScoringRule> => {
    const response = await apiClient.post<ScoringRule>('/api/scoring/rules', data);
    return response.data;
  },

  // スコアルール詳細取得
  getRule: async (ruleId: number): Promise<ScoringRule> => {
    const response = await apiClient.get<ScoringRule>(`/api/scoring/rules/${ruleId}`);
    return response.data;
  },

  // スコアルール更新
  updateRule: async (ruleId: number, data: ScoringRuleCreate): Promise<ScoringRule> => {
    const response = await apiClient.put<ScoringRule>(`/api/scoring/rules/${ruleId}`, data);
    return response.data;
  },

  // スコアルール削除
  deleteRule: async (ruleId: number): Promise<void> => {
    await apiClient.delete(`/api/scoring/rules/${ruleId}`);
  },

  // スコア計算
  calculate: async (params: ScoringCalculateParams): Promise<ScoringResultResponse> => {
    const response = await apiClient.get<ScoringResultResponse>('/api/scoring/calculate', { params });
    return response.data;
  },

  // スコアランキング取得
  getRanking: async (params: ScoringRankingParams): Promise<ScoreRankingResponse> => {
    const response = await apiClient.get<ScoreRankingResponse>('/api/scoring/ranking', { params });
    return response.data;
  },
};

// コンバージョンAPIパラメータ
export interface ConversionTrendParams {
  goal_id: number;
  start_date: string;
  end_date: string;
  aggregation?: 'daily' | 'weekly' | 'monthly';
  account_ids?: number[];
}

export interface ConversionSummaryParams {
  company_id: number;
  start_date: string;
  end_date: string;
  account_ids?: number[];
}

export const conversionApi = {
  // コンバージョンゴール一覧取得
  getGoals: async (companyId: number, isActive?: boolean): Promise<ConversionGoal[]> => {
    const params: Record<string, unknown> = { company_id: companyId };
    if (isActive !== undefined) {
      params.is_active = isActive;
    }
    const response = await apiClient.get<ConversionGoal[]>('/api/conversion/goals', { params });
    return response.data;
  },

  // コンバージョンゴール作成
  createGoal: async (data: ConversionGoalCreate): Promise<ConversionGoal> => {
    const response = await apiClient.post<ConversionGoal>('/api/conversion/goals', data);
    return response.data;
  },

  // コンバージョンゴール詳細取得
  getGoal: async (goalId: number): Promise<ConversionGoal> => {
    const response = await apiClient.get<ConversionGoal>(`/api/conversion/goals/${goalId}`);
    return response.data;
  },

  // コンバージョンゴール更新
  updateGoal: async (goalId: number, data: ConversionGoalCreate): Promise<ConversionGoal> => {
    const response = await apiClient.put<ConversionGoal>(`/api/conversion/goals/${goalId}`, data);
    return response.data;
  },

  // コンバージョンゴール削除
  deleteGoal: async (goalId: number): Promise<void> => {
    await apiClient.delete(`/api/conversion/goals/${goalId}`);
  },

  // コンバージョン推移取得
  getTrend: async (params: ConversionTrendParams): Promise<ConversionTrendResponse> => {
    const response = await apiClient.get<ConversionTrendResponse>('/api/conversion/trend', { params });
    return response.data;
  },

  // コンバージョンサマリー取得
  getSummary: async (params: ConversionSummaryParams): Promise<ConversionSummaryResponse> => {
    const response = await apiClient.get<ConversionSummaryResponse>('/api/conversion/summary', { params });
    return response.data;
  },
};
